import { ScreenOrientation } from '@capgo/capacitor-screen-orientation';
import { isPlatform } from '@ionic/react';

/**
 * Phones swap native element fullscreen for a rotation into the landscape
 * layout (the YouTube pattern): portrait video already spans the width, so
 * native fullscreen adds nothing there, and iOS needed a glitchy inset
 * recovery workaround after exiting it. Desktop and tablets keep true
 * element fullscreen — tablets never get the landscape phone layout (their
 * viewports are taller than its 599px gate), and desktops can't rotate.
 */
export const ROTATION_FULLSCREEN = isPlatform('capacitor') && !isPlatform('tablet') && !isPlatform('ipad');

type TargetOrientation = 'landscape' | 'portrait';

const PHYSICAL_ORIENTATION_POLL_MS = 500;

// The tracker's accelerometer classifier flips to the target reading around
// 30-45 degrees into a turn, well before the OS's own auto-rotation
// threshold (~60 degrees with hysteresis). Unlocking at the raw flip lets
// the OS — which still considers the device at the origin — snap the UI
// back mid-turn. Only release once the reading has held this long; a normal
// turn has finished (and the OS agrees) well within it.
const RELEASE_SETTLE_MS = 900;

// A reading that visited the target for at least this long before turning
// back counts as a deliberate "rotated there and back" exit gesture; shorter
// visits are sensor flicker from a jiggled phone and must not exit.
const TARGET_HOLD_EXIT_MS = 250;

let releaseRotationLock: (() => void) | null = null;

/**
 * Rotate the video UI by locking the interface orientation, then release the
 * lock once motion tracking shows a deliberate physical rotation — from then
 * on natural rotation governs, so physically rotating the device back is
 * what exits, exactly like the YouTube app.
 */
export const rotateVideoTo = async (target: TargetOrientation) => {
    releaseVideoRotation();

    await ScreenOrientation.lock({ orientation: target });
    await ScreenOrientation.startOrientationTracking({ bypassOrientationLock: true });

    // Release rules, driven by the physical readings:
    // - away from target, then holding at target for the settle period: the
    //   user completed the turn; release so natural rotation governs and
    //   turning back exits. (Not on the first target reading — see
    //   RELEASE_SETTLE_MS — and not at all for a sub-settle flicker.)
    // - back away after the readings started at the target (they tapped the
    //   button mid-turn) or after a held visit to the target: a deliberate
    //   there-and-back gesture; release immediately, which snaps the UI back
    //   — the exit they asked for. Releasing toward the origin can't glitch:
    //   the OS either agrees already or finishes the turn the user is making.
    // Never releasing while the readings sit unchanged at the target
    // protects users whose system rotation lock makes physical match the
    // target from the very start (e.g. lying on their side).
    let sawAway = false;
    let startedAtTarget: boolean | undefined;
    let targetSince: number | undefined;
    let settleTimer: number | undefined;

    const clearSettleTimer = () => {
        if (settleTimer !== undefined) {
            window.clearTimeout(settleTimer);
            settleTimer = undefined;
        }
    };

    const onPhysicalReading = (physicalOrientation: string | undefined) => {
        if (!physicalOrientation) {
            return;
        }

        const atTarget = physicalOrientation.startsWith(target);
        if (startedAtTarget === undefined) {
            startedAtTarget = atTarget;
        }

        if (atTarget) {
            targetSince ??= Date.now();
            if (sawAway && settleTimer === undefined) {
                settleTimer = window.setTimeout(() => releaseVideoRotation(), RELEASE_SETTLE_MS);
            }
        } else {
            clearSettleTimer();
            const heldTarget = targetSince !== undefined && Date.now() - targetSince >= TARGET_HOLD_EXIT_MS;
            targetSince = undefined;
            sawAway = true;

            if (startedAtTarget || heldTarget) {
                releaseVideoRotation();
            }
        }
    };

    // While tracking is active both platforms suppress UI-sourced change
    // events, so the listener delivers pure physical readings with sensor
    // latency; the poll seeds the state and covers a missed event.
    const listener = await ScreenOrientation.addListener('screenOrientationChange', ({ type }) => onPhysicalReading(type));

    const poll = window.setInterval(() => {
        ScreenOrientation.isOrientationLocked()
            .then(({ physicalOrientation }) => onPhysicalReading(physicalOrientation))
            // a failed read must not strand the lock; just poll again
            .catch(() => undefined);
    }, PHYSICAL_ORIENTATION_POLL_MS);

    releaseRotationLock = () => {
        releaseRotationLock = null;
        clearSettleTimer();
        window.clearInterval(poll);
        listener.remove().catch(() => undefined);
        ScreenOrientation.stopOrientationTracking().catch(() => undefined);
        ScreenOrientation.unlock().catch(() => undefined);
    };
};

/**
 * Drop any held orientation lock and stop motion tracking. Safe to call
 * unconditionally (e.g. on player unmount).
 */
export const releaseVideoRotation = () => {
    releaseRotationLock?.();
};
